import React, { useEffect, useState } from "react";
import { LessonPlan } from "../../../components/study/course/LessonPlan";
import Loader from "../../../components/Loader";
import { CourseComplete, Section } from "../../../components/study/course";
import { useStudy } from "../hooks";
import { useCourse } from "../../course/hooks";
import { useQuiz } from "../../quiz/hooks";
import { useIRGrade } from "../../irGrading/hooks";
import { getNextCourse } from "../../../store/course/courseBaseApi";
import { chapterPassed } from "./gateUtils";
import { ensureCourseAssigned } from "./assignUtils";

export const Course = ({
  course,
  lessonPlan,
  setShowCourseOutline,
  summaries,
  activities,
  gate,
  refreshGate,
}) => {
  const { setProgress, profile, updateProfile } = useStudy();
  const {
    setCurrentSession: setCurrentSectionProgress,
    currentCourseComplete,
    setCurrentCourseCompleted,
    setNextCourse,
    setAllCourseComplete,
  } = useCourse();

  const { updateCurrentCourseDetails } = useQuiz();
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [currentSection, setCurrentSection] = useState({});
  // const [isCourseComplete, setIsCourseComplete] = useState(true);
  const [totalSections, setTotalSections] = useState(
    lessonPlan?.sections?.length || 0,
  );
  const [sections, setSections] = React.useState([]);
  const [activityData, setActivityData] = useState([]);
  const [chaptersCompleted, setChaptersCompleted] = React.useState([]);

  const onSectionCompletion = () => {
    if (currentSectionIndex < totalSections) {
      setCurrentSectionIndex(currentSectionIndex + 1);
    }
  };

  useEffect(() => {
    if (lessonPlan?.sections?.length) {
      setTotalSections(lessonPlan.sections.length);
      setProgress({ section: lessonPlan.sections[0].sectionId });
    }
  }, [lessonPlan]);

  useEffect(() => {
    if (setShowCourseOutline && typeof setShowCourseOutline === "function") {
      setShowCourseOutline(!currentCourseComplete);
    }
  }, [currentCourseComplete]);

  useEffect(() => {
    if (!activities || !activities.length) {
      activities = [];
    } else {
      setActivityData(activities);
    }

    if (!summaries || !summaries.length) summaries = [];

    // The per-session gate is the authority for chapter completion: a
    // chapter counts as done only when every one of its sessions PASSED
    // (>= threshold, or attempted-but-unscoreable) — not merely when some
    // activity was logged for it. Never fall back to the old permissive
    // activity-log set while the gate loads: that would flash-unlock
    // chapters the gate is about to lock.
    if (!gate) return;

    let _sections = lessonPlan.sections.map((e) => e); //Creating a copy from the state as the global state objects are read-only
    let completedChapters = new Set();

    let allSectionsCompleted = true; // Track course completion

    let currSectionInd = null;

    _sections = _sections.map((e) => {
      let completed = false,
        chaptersCompletedCount = 0,
        totalChapters = e.chapters.length;

      let chapters = e.chapters.map((it, i) => {
        let chapterCompleted = chapterPassed(gate, e.sectionId, it.chapterId);
        if (chapterCompleted) {
          completedChapters.add(it.chapterId);
          // Count only an unbroken passed prefix (linear lock): a failed
          // chapter followed by a "completed" later one must still resume
          // ON the failed chapter, not after it.
          if (chaptersCompletedCount === i) chaptersCompletedCount = i + 1;
        }

        return { ...it, completed: chapterCompleted };
      });

      let sectionCompleted = chaptersCompletedCount === totalChapters;
      if (!sectionCompleted) allSectionsCompleted = false; // If any section is incomplete, course isn't complete

      if (chaptersCompletedCount === totalChapters) completed = true;

      return { ...e, completed, chapters };
    });

    setChaptersCompleted([...completedChapters]);

    for (let index = 0; index < _sections.length; index++) {
      const _sec = _sections[index];

      if (!_sec.completed) {
        currSectionInd = index;
        break;
      }
    }

    setSections(_sections);

    // Course completion also requires section-level assessment sessions
    // (not tied to any chapter) — gate.eligible covers the whole course.
    if (allSectionsCompleted && gate.eligible && !currentCourseComplete) {
      setCurrentCourseCompleted(true);
      onCourseComplete();
      return;
    }

    currSectionInd && setCurrentSectionIndex(currSectionInd);
    setCurrentSection(_sections[currSectionInd ?? 0]);
    setCurrentSectionProgress(_sections[currSectionInd ?? 0]);
  }, [activities, summaries, gate]);

  const loadAndAssignCourse = async (courseId) => {
    // Awaited, idempotent assign (see assignUtils). The old saga dispatch
    // here was fire-and-forget: an error (or the level-up hard navigation)
    // could silently lose the POST, leaving the student's assignments
    // ending at the previous level's course — which is what login opens.
    try {
      await ensureCourseAssigned(profile.id, courseId);
    } catch (e) {
      console.error(
        "[Course→Next] assigning next course failed — Continue will retry",
        e,
      );
    }
  };

  const loadNextCourseData = async (level, id, _courseId) => {
    const { data } = await getNextCourse({
      level,
      studentId: id,
      courseId: _courseId,
    });
    setNextCourse(data);
    return data;
  };

  const assignLevel = async (level) => {
    updateProfile({
      body: { gradeLevel: level },
      role: "students", //   Hardcoding the role as of now
      id: profile.id,
    });
  };

  const onCourseComplete = async () => {
    let studentId = profile?.id;
    const level = profile.gradeLevel;
    let currentCourseId = course?.id;
    console.log("[Course→Next] onCourseComplete fired", {
      studentId,
      studentName: profile?.userName || profile?.firstName,
      geographicalType: profile?.geographicalType,
      currentLevel: level,
      currentCourseId,
      currentCourseName: course?.courseName,
    });
    if (!level) {
      // Don't fire next-ir-level with an empty level — the backend can't
      // find a current-level mapping and returns nothing useful, and the
      // empty query param produces ugly URLs in the network panel. Defer
      // to Study.jsx's retry-on-load when the student returns.
      console.warn(
        "[Course→Next] profile.gradeLevel is empty — skipping next-ir-level. " +
        "Student will go through Study.jsx's /level-course resolver on next load.",
      );
      setCurrentCourseCompleted(true);
      return;
    }
    if (studentId && currentCourseId) {
      let nxtCourse = await loadNextCourseData(
        level,
        studentId,
        currentCourseId,
      );
      console.log("[Course→Next] /next-ir-level response", {
        response: nxtCourse,
        newLevel: nxtCourse?.newLevel,
        eligible: nxtCourse?.eligible,
        noCourseForStudentType: nxtCourse?.noCourseForStudentType,
        nextLevel: nxtCourse?.nextLevel,
        nextCourseId: nxtCourse?.nextLevel?.irCourseId,
        nextLevelLabel: nxtCourse?.nextLevel?.level,
      });
      if (!nxtCourse) {
        console.warn(
          "[Course→Next] backend returned nothing — treating as ALL COURSES COMPLETE",
        );
        setCurrentCourseCompleted(true);
        setAllCourseComplete(true);
        return;
      }

      // Level gate blocked with the retake target being THIS course (the
      // backend points nextLevel at the first course with a failing session).
      // Don't re-assign a course the student already has — just show the
      // completion screen; Continue re-enters the course and the in-course
      // linear lock lands them on the failing session.
      const isBlockedOnThisCourse =
        nxtCourse?.eligible === false &&
        nxtCourse?.newLevel === false &&
        nxtCourse?.nextLevel?.irCourseId === currentCourseId;

      if (isBlockedOnThisCourse) {
        console.warn(
          "[Course→Next] level gate not passed — retake target is the current course",
        );
        setCurrentCourseCompleted(true);
        return;
      }

      if (nxtCourse.nextLevel && nxtCourse.nextLevel.irCourseId) {
        let _nextCourseId = nxtCourse.nextLevel.irCourseId;
        console.log("[Course→Next] ✅ assigning next course", _nextCourseId);
        // Assign the new course server-side so it's in the student's
        // assignments by the time they click Continue. Do NOT update the
        // local profile.gradeLevel yet — that would trigger Study.jsx's
        // [profile] effect and swap CourseTreeView's courseId prop out
        // from under the user before they ever see the "Proceed" screen.
        // gradeLevel is bumped in CourseComplete#onContinue instead.
        await loadAndAssignCourse(_nextCourseId);
      } else if (nxtCourse.noCourseForStudentType) {
        console.warn(
          "[Course→Next] NO COURSE for this student type at the next level — admin must author a",
          (profile?.geographicalType || "URBAN").toUpperCase(),
          "course in /course-mapping.",
        );
        setCurrentCourseCompleted(true);
      } else {
        console.log(
          "[Course→Next] no nextLevel and no flag — student has finished all levels",
        );
        setCurrentCourseCompleted(true);
        setAllCourseComplete(true);
      }
    } else {
      console.warn("[Course→Next] missing studentId or currentCourseId — skipping", {
        studentId,
        currentCourseId,
      });
    }
  };

  useEffect(() => {
    if (!sections[currentSectionIndex]) return;
    setProgress({ section: sections[currentSectionIndex].sectionId });
    setCurrentSectionProgress(sections[currentSectionIndex]);
    updateCurrentCourseDetails({
      sectionId: sections[currentSectionIndex].sectionId,
      courseId: course.id,
    });
  }, [currentSectionIndex]);

  if (
    !Object.keys(lessonPlan).length ||
    !Object.keys(course).length ||
    !sections.length ||
    !Object.keys(currentSection).length
  ) {
    return <Loader />;
  }

  return (
    <div style={styles.container}>
      {/* <h1>{course.courseName}{sections[currentSectionIndex] && (', ' + sections[currentSectionIndex]['name'])}</h1> */}
      {!currentCourseComplete && Object.keys(currentSection).length > 0 && (
        <Section
          onSectionCompletion={onSectionCompletion}
          currentSectionData={sections[currentSectionIndex]}
          currentSection={currentSectionIndex}
          totalSections={totalSections}
          onCourseComplete={onCourseComplete}
          course={course}
          chaptersCompleted={chaptersCompleted}
          data={sections[currentSectionIndex]}
          activities={activityData}
          nextSection={sections[currentSectionIndex + 1]}
          gate={gate}
          refreshGate={refreshGate}
        />

      )}
    </div>
  );
};

const styles = {
  container: {
    height: "100%",
  },
};
